import { useState, useEffect, useCallback } from "react"

const TOAST_LIMIT = 1

let count = 0
function generateId() {
  count = (count + 1) % Number.MAX_VALUE
  return count.toString()
}

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastProps {
  id?: string
  title?: string
  description?: string
  action?: ToastAction
  variant?: "default" | "destructive"
  duration?: number
  dismiss?: () => void
  update?: (props: Partial<ToastProps>) => void
}

interface ToastState {
  toasts: ToastProps[]
}

type ToastListener = (state: ToastState) => void

const toastStore = {
  state: {
    toasts: [] as ToastProps[],
  },
  listeners: [] as ToastListener[],

  getState: () => toastStore.state,

  setState: (nextState: Partial<ToastState> | ((state: ToastState) => ToastState)) => {
    if (typeof nextState === 'function') {
      toastStore.state = nextState(toastStore.state)
    } else {
      toastStore.state = { ...toastStore.state, ...nextState }
    }
    toastStore.listeners.forEach(listener => listener(toastStore.state))
  },

  subscribe: (listener: ToastListener) => {
    toastStore.listeners.push(listener)
    return () => {
      toastStore.listeners = toastStore.listeners.filter(l => l !== listener)
    }
  }
}

export const toast = (props: ToastProps) => {
  const id = generateId()

  const update = (updateProps: Partial<ToastProps>) =>
    toastStore.setState((state) => ({
      ...state,
      toasts: state.toasts.map((t: ToastProps) =>
        t.id === id ? { ...t, ...updateProps } : t
      ),
    }))

  const dismiss = () => toastStore.setState((state) => ({
    ...state,
    toasts: state.toasts.filter((t: ToastProps) => t.id !== id),
  }))

  toastStore.setState((state) => ({
    ...state,
    toasts: [
      { ...props, id, dismiss },
      ...state.toasts,
    ].slice(0, TOAST_LIMIT),
  }))

  return {
    id,
    dismiss,
    update,
  }
}

export function useToast() {
  const [state, setState] = useState<ToastState>(toastStore.getState())

  useEffect(() => {
    const unsubscribe = toastStore.subscribe((newState: ToastState) => {
      setState(newState)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = []

    state.toasts.forEach((toastItem: ToastProps) => {
      if (toastItem.duration === Infinity) {
        return
      }
      const timeout = setTimeout(() => {
        toastItem.dismiss?.()
      }, toastItem.duration || 5000)
      timeouts.push(timeout)
    })

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout))
    }
  }, [state.toasts])

  return {
    toast,
    toasts: state.toasts,
  }
}
